import DefaultPaginationConfig from "@/constants/pagination";
import { CompositeFilterDescriptor } from "@progress/kendo-data-query";
import { CustomFilter } from "../interfaces/columns.interface";
import { isAuthorize } from "./common";
import { SUPER_ADMIN } from "@/constants/roles";

export const MIN_RESIZABLE_WIDTH_KENDO_TABLE = 80;

export const getPaginationData = () => {
  let Pagination: any = sessionStorage.getItem("Pagination");

  if (!Pagination) {
    Pagination = DefaultPaginationConfig;
  } else {
    Pagination = JSON.parse(Pagination);
  }

  return Pagination;
};

export const parseKendoFilter = (kendoFilter: any) => {
  let filterString = "";
  if (kendoFilter && kendoFilter.filter) {
    filterString = parseKendoLogicalFilter(kendoFilter.filter);
  }
  return filterString;
};

const parseKendoLogicalFilter = (filter: any) => {
  let logicalFilter = "";

  if (!filter?.filters?.length) return "";

  if (filter && filter.filters && filter.filters.length > 0) {
    for (let i = 0; i < filter.filters.length; i++) {
      const currentFilter = filter.filters[i];
      if (
        currentFilter.logic &&
        currentFilter.filters &&
        currentFilter.filters.length > 0
      ) {
        const subFilterString = parseKendoLogicalFilter(currentFilter);
        logicalFilter += `${subFilterString}`;
      } else if (currentFilter.field && currentFilter.operator) {
        logicalFilter += getQuery(currentFilter);
      }
      if (i < filter.filters.length - 1) {
        logicalFilter += ` ${filter.logic} `;
      }
    }
  }

  return logicalFilter;
};

const getQuery = (query: any) => {
  const operator = query.operator;
  const typeOfValue = typeof query.value;
  const value =
    typeOfValue === "number" || typeOfValue === "boolean"
      ? query.value
      : `'${query.value}'`;
  switch (operator) {
    case "contains":
      return `contains(${query.field}, ${value})`;
    case "doesnotcontain":
      return `not(contains(${query.field}, ${value})`;

    case "startswith":
      return `startsWith(${query.field}, ${value})`;
    case "eq":
      return `${query.field} eq ${value}`;
    case "neq":
      return `not(${query.field} eq ${value})`;
    default:
      return "";
  }
};

export const levelFilters = (
  isAdmin: boolean,
  selectedProjectId?: string,
  dataStateFilter?: CompositeFilterDescriptor | null,
  showArchived: boolean = false,
  additionalFilters: CustomFilter[] = []
): CompositeFilterDescriptor => {
  const filters: any[] = [];

  // If user is not admin only bring published levels
  if (!isAdmin) {
    filters.push({
      logic: "and",
      filters: [
        {
          field: "status",
          operator: "eq",
          value: "Published",
        },
      ],
    });
  }

  if (isAdmin) {
    filters.push({
      logic: "and",
      filters: [
        {
          field: "parentLevelId",
          operator: "eq",
          value: "",
        },
      ],
    });
  }

  // Project filter
  if (selectedProjectId && selectedProjectId !== "all") {
    filters.push({
      logic: "and",
      filters: [
        {
          field: "projectId",
          operator: "eq",
          value: selectedProjectId,
        },
      ],
    });
  }

  // Active/Archived filter
  if (!showArchived) {
    filters.push({
      logic: "and",
      filters: [
        {
          field: "isActive",
          operator: "eq",
          value: true,
        },
      ],
    });
  }

  // Process additional filters
  if (additionalFilters.length > 0) {
    additionalFilters.forEach((filter) => {
      filters.push({
        logic: filter.logic || "and",
        filters: [
          {
            field: filter.field,
            operator: filter.operator,
            value: filter.value,
          },
        ],
      });
    });
  }

  // Add existing filters from dataState if any
  if (dataStateFilter?.filters?.length) {
    dataStateFilter.filters.forEach((filter) => {
      if ("logic" in filter && "filters" in filter) {
        filters.push(filter);
      } else {
        filters.push({
          logic: "and",
          filters: [filter],
        });
      }
    });
  }

  return {
    logic: "and",
    filters,
  };
};

export const buildFilters = (
  selectedProjectId?: string | null,
  dataStateFilter?: CompositeFilterDescriptor | null,
  showArchived: boolean = false,
  additionalFilters: CustomFilter[] = []
): CompositeFilterDescriptor => {
  const filters: any[] = [];

  // Project filter
  if (selectedProjectId && selectedProjectId !== "all") {
    filters.push({
      logic: "and",
      filters: [
        {
          field: "projectId",
          operator: "eq",
          value: selectedProjectId,
        },
      ],
    });
  }

  // Active/Archived filter
  if (!showArchived) {
    filters.push({
      logic: "and",
      filters: [
        {
          field: "isActive",
          operator: "eq",
          value: true,
        },
      ],
    });
  }

  // Process additional filters
  if (additionalFilters.length > 0) {
    additionalFilters.forEach((filter) => {
      if (filter.field && filter.operator === "array-contains") {
        filters.push({
          logic: "and",
          filters: [
            {
              field: "roles",
              operator: "array-contains",
              value: filter.value,
            },
          ],
        });
      } else
        filters.push({
          logic: filter.logic || "and",
          filters: [
            {
              field: filter.field,
              operator: filter.operator,
              value: filter.value,
            },
          ],
        });
    });
  }

  // Add existing filters from dataState if any
  if (dataStateFilter?.filters?.length) {
    dataStateFilter.filters.forEach((filter) => {
      if ("logic" in filter && "filters" in filter) {
        filters.push(filter);
      } else {
        filters.push({
          logic: "and",
          filters: [filter],
        });
      }
    });
  }

  return {
    logic: "and",
    filters,
  };
};

export const buildUserGroupFilters = (
  selectedProjectId?: string | null,
  dataStateFilter?: CompositeFilterDescriptor | null,
  showArchived: boolean = false
): CompositeFilterDescriptor => {
  const filters: any[] = [];

  // Project filter using array-contains for Projects field
  if (selectedProjectId && selectedProjectId !== "all") {
    filters.push({
      logic: "and",
      filters: [
        {
          field: "projects",
          operator: "array-contains",
          value: selectedProjectId,
        },
      ],
    });
  }

  // Active/Archived filter
  if (!showArchived) {
    filters.push({
      logic: "and",
      filters: [
        {
          field: "isActive",
          operator: "eq",
          value: true,
        },
      ],
    });
  }

  // Add existing filters from dataState if any
  if (dataStateFilter?.filters?.length) {
    dataStateFilter.filters.forEach((filter) => {
      if ("logic" in filter && "filters" in filter) {
        filters.push(filter);
      } else {
        filters.push({
          logic: "and",
          filters: [filter],
        });
      }
    });
  }

  return {
    logic: "and",
    filters,
  };
};

export const companyFilters = (
  type: string,
  projectId?: string,
  dataStateFilter?: CompositeFilterDescriptor | null,
  showArchived: boolean = false
): CompositeFilterDescriptor => {
  const filters: any[] = [];

  // Project filter
  if (projectId) {
    const projectOperator = showArchived ? "project-id-contains" : "project-active-contains";
    // if user is super admin bring super admins for selected project users
    if (isAuthorize([SUPER_ADMIN])) {
      filters.push({
        logic: "or",
        filters: [
          {
            field: "Projects",
            operator: projectOperator,
            value: projectId,
          },
          {
            field: "Roles",
            operator: "array-contains",
            value: "Super Admin",
          },
        ],
      });
    }
    // if user is not super admin bring only users for selected project
    else {
      filters.push({
        logic: "and",
        filters: [
          {
            field: "Projects",
            operator: projectOperator,
            value: projectId,
          },
        ],
      });
    }
  }

  // Add filter for roles if type is "user"
  if (type === "user") {
    if (!isAuthorize([SUPER_ADMIN])) {
      filters.push({
        logic: "and",
        filters: [
          {
            field: "Roles",
            operator: "array-doesnotcontain",
            value: "Super Admin",
          },
        ],
      });
    }
  }

  // Add existing filters from dataState if any
  if (dataStateFilter?.filters?.length) {
    dataStateFilter.filters.forEach((filter) => {
      if ("logic" in filter && "filters" in filter) {
        filters.push(filter);
      } else {
        filters.push({
          logic: "and",
          filters: [filter],
        });
      }
    });
  }

  return {
    logic: "and",
    filters,
  };
};

export const notificationFilters = (
  userId?: string
): CompositeFilterDescriptor => {
  const filters: any[] = [];

  // Project filter
  if (userId) {
    filters.push({
      logic: "and",
      filters: [
        {
          field: "UserId",
          operator: "eq",
          value: userId,
        },
        {
          field: "IsActive",
          operator: "eq",
          value: true,
        },
      ],
    });
  }

  return {
    logic: "and",
    filters,
  };
};

export const ExistingFilters = (
  type: string,
  projectId?: string
): CompositeFilterDescriptor => {
  const filters: any[] = [];

  // Handle both single string and array of strings
  const idsArray = [projectId];

  // Project filter
  if (projectId) {
    filters.push({
      logic: "and",
      filters: [
        {
          field: "Projects",
          operator: "project-active-doesnotcontain",
          value: projectId,
        },
      ],
    });
  }

  // Add filter for roles if type is "user"
  if (type === "user") {
    filters.push({
      logic: "and",
      filters: [
        {
          field: "Roles",
          operator: "array-doesnotcontain",
          value: "Super Admin",
        },
      ],
    });
  }

  return {
    logic: "and",
    filters,
  };
};

export const companyUserFilter = (
  projectId?: string
): CompositeFilterDescriptor => {
  const filters: any[] = [];

  // Handle both single string and array of strings
  const idsArray = [projectId];

  // Project filter
  if (projectId) {
    filters.push({
      logic: "and",
      filters: [
        {
          field: "Projects",
          operator: "project-active-contains",
          value: projectId,
        },
      ],
    });
  }

  return {
    logic: "and",
    filters,
  };
};
